import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import {
    MessageSquare,
    Plus,
    Newspaper,
    BookOpen,
    LogOut,
    User,
    Trash2,
    Edit3,
    Loader2,
    RefreshCw,
    Image as ImageIcon,
    Clock,
    Calendar,
    X,
    AlertCircle,
    CheckCircle2,
    Search,
    Link as LinkIcon
} from 'lucide-react';



const Dashboard = ({ user, onLogout }) => {
    const [activeTab, setActiveTab] = useState('news'); // 'news', 'research', or 'messages'
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [editingItem, setEditingItem] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Helper for today's date in YYYY-MM-DD
    const getToday = () => new Date().toISOString().split('T')[0];

    // News Form State
    const [newsForm, setNewsForm] = useState({
        title: '',
        category: '',
        date: getToday(),
        summary: '',
        url: ''
    });

    // Research Form State
    const [researchForm, setResearchForm] = useState({
        title: '',
        source: '',
        year: new Date().getFullYear().toString(),
        summary: '',
        url: ''
    });

    const fetchData = async () => {
        setLoading(true);
        setMessage({ text: '', type: '' });
        try {
            const endpoint = activeTab;
            const defaultUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';
            const API_URL = process.env.REACT_APP_API_URL || defaultUrl;
            const response = await fetch(`${API_URL}/api/${endpoint}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.status === 401 || response.status === 403) {
                setMessage({ text: 'Session expired. Please log in again.', type: 'error' });
                setTimeout(() => onLogout(), 2000);
                return;
            }

            if (!response.ok) throw new Error('Failed to fetch data');
            const data = await response.json();

            // Sort by timestamp descending (newest first)
            const sortedData = Array.isArray(data) ? data.sort((a, b) => {
                const dateA = new Date(a.timestamp || a.date || 0);
                const dateB = new Date(b.timestamp || b.date || 0);
                return dateB - dateA;
            }) : [];

            setItems(sortedData);
        } catch (err) {
            setMessage({ text: 'Error connecting to server. Please check your backend.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        cancelEdit();
    }, [activeTab]);

    const cancelEdit = () => {
        setEditingItem(null);
        setMessage({ text: '', type: '' });
        if (activeTab === 'news') {
            setNewsForm({
                title: '',
                category: '',
                date: getToday(),
                summary: '',
                url: ''
            });
        } else if (activeTab === 'research') {
            setResearchForm({
                title: '',
                source: '',
                year: new Date().getFullYear().toString(),
                summary: '',
                url: ''
            });
        }
    };

    const startEdit = (item) => {
        setEditingItem(item);
        if (activeTab === 'news') {
            setNewsForm({
                title: item.title,
                category: item.category,
                date: item.date,
                summary: item.summary,
                url: item.url || ''
            });
        } else {
            setResearchForm({
                title: item.title,
                source: item.source,
                year: item.year,
                summary: item.summary,
                url: item.url || ''
            });
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        if (!token) {
            setMessage({ text: 'Session expired. Please log in again.', type: 'error' });
            return;
        }

        setSubmitting(true);
        setMessage({ text: '', type: '' });

        const endpoint = activeTab;
        const body = activeTab === 'news' ? { ...newsForm } : { ...researchForm };


        if (editingItem) {
            body.id = editingItem.id;
            body.timestamp = editingItem.timestamp;
        }

        try {
            const defaultUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';
            const API_URL = process.env.REACT_APP_API_URL || defaultUrl;
            const url = editingItem
                ? `${API_URL}/api/${endpoint}/${editingItem.id}`
                : `${API_URL}/api/${endpoint}`;

            const response = await fetch(url, {
                method: editingItem ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(body),
            });

            if (response.ok) {
                setMessage({
                    text: `Successfully ${editingItem ? 'updated' : 'added'} the entry.`,
                    type: 'success'
                });
                cancelEdit();
                fetchData();
            } else {
                let errorMsg = 'Operation failed.';
                try {
                    const errData = await response.json();
                    errorMsg = errData.detail || errorMsg;
                } catch (je) { }
                setMessage({ text: errorMsg, type: 'error' });
            }
        } catch (err) {
            setMessage({ text: 'Connection refused. Is the API server running?', type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const filteredItems = items.filter(item =>
        item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.summary?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.source?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.message?.toLowerCase().includes(searchQuery.toLowerCase())
    );


    const handleDelete = async (id) => {
        if (!window.confirm('Delete this permanently?')) return;

        try {
            const defaultUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';
            const API_URL = process.env.REACT_APP_API_URL || defaultUrl;
            const endpoint = activeTab;
            const response = await fetch(`${API_URL}/api/${endpoint}/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                fetchData();
                if (editingItem && editingItem.id === id) cancelEdit();
            } else {
                alert('Deletion failed.');
            }
        } catch (err) {
            console.error('Delete error', err);
        }
    };

    return (
        <div className="min-h-screen bg-[#F6F6F6] flex flex-col">
            {/* Horizontal Nav Bar */}
            <nav className="bg-white border-b border-[#D6E4F0] sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center space-x-8">
                        <div className="flex items-center space-x-4 pr-8 border-r border-[#D6E4F0]">
                            <div className="w-12 h-12 rounded-full overflow-hidden shadow-sm border border-[#D6E4F0] bg-white flex-shrink-0">
                                <img
                                    src="/logo.png"
                                    alt="Sage logo"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="flex flex-col">
                                <span className="font-extrabold text-xl tracking-tight leading-none" style={{ fontFamily: 'Merriweather, serif', color: '#163172' }}>
                                    Sage and Seed
                                </span>
                                <div className="mt-1.5 flex">
                                    <span className="text-[9px] font-black uppercase tracking-[0.2em] px-2 py-0.5 rounded-md text-white shadow-sm" style={{ background: '#1E56A0' }}>
                                        Admin
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center space-x-1">
                            <button
                                onClick={() => setActiveTab('news')}
                                className={`flex items-center space-x-2 px-6 py-2 rounded-full transition-all text-sm font-bold ${activeTab === 'news' ? 'bg-[#1E56A0] text-white shadow-md' : 'text-[#5a6c7d] hover:bg-[#F6F6F6]'}`}
                            >
                                <Newspaper className="w-4 h-4" />
                                <span>News</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('research')}
                                className={`flex items-center space-x-2 px-6 py-2 rounded-full transition-all text-sm font-bold ${activeTab === 'research' ? 'bg-[#1E56A0] text-white shadow-md' : 'text-[#5a6c7d] hover:bg-[#F6F6F6]'}`}
                            >
                                <BookOpen className="w-4 h-4" />
                                <span>Research</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('messages')}
                                className={`flex items-center space-x-2 px-6 py-2 rounded-full transition-all text-sm font-bold ${activeTab === 'messages' ? 'bg-[#1E56A0] text-white shadow-md' : 'text-[#5a6c7d] hover:bg-[#F6F6F6]'}`}
                            >
                                <MessageSquare className="w-4 h-4" />
                                <span>Messages</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center space-x-6">
                        <div className="flex items-center space-x-3 text-right">
                            <div className="hidden sm:block">
                                <p className="text-sm font-bold" style={{ color: '#163172' }}>{user.full_name}</p>
                                <p className="text-xs text-[#5a6c7d]">{user.email}</p>
                            </div>
                            <div className="w-10 h-10 rounded-full bg-[#E5E7EB] flex items-center justify-center text-[#163172]">
                                <User className="w-5 h-5" />
                            </div>
                        </div>
                        <button
                            onClick={onLogout}
                            className="px-4 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-all font-bold text-sm flex items-center space-x-2"
                        >
                            <LogOut className="w-4 h-4" />
                            <span>Sign Out</span>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Main Content Area */}
            <main className="max-w-7xl mx-auto px-6 py-12 w-full">
                <header className="flex flex-col md:flex-row md:items-end justify-between mb-12 space-y-4 md:space-y-0">
                    <div>
                        <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: 'Merriweather, serif', color: '#163172' }}>
                            {activeTab === 'news' ? 'News Engine' : activeTab === 'research' ? 'Research Library' : 'Inbox Messages'}
                        </h1>
                        <p className="text-lg" style={{ color: '#5a6c7d' }}>
                            {activeTab === 'news'
                                ? 'Manage the latest stories and updates featured on the homepage.'
                                : activeTab === 'research'
                                    ? 'Curate academic publications and evidence-based findings.'
                                    : 'Review and respond to inquiries from the contact form.'}
                        </p>
                    </div>

                    <Button
                        onClick={fetchData}
                        disabled={loading}
                        className="rounded-xl flex items-center space-x-2 px-6 py-4 bg-white hover:bg-slate-50 border border-[#D6E4F0] text-[#163172] shadow-sm self-start transition-all"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        <span className="font-bold">Sync Data</span>
                    </Button>
                </header>

                <div className={`grid grid-cols-1 ${activeTab === 'messages' ? 'lg:grid-cols-1' : 'lg:grid-cols-2'} gap-10 items-start`}>
                    {/* Form Card (Hidden for Messages) */}
                    {activeTab !== 'messages' && (
                        <div className="bg-white p-10 rounded-[32px] shadow-sm border border-[#D6E4F0]">
                            <div className="flex items-center justify-between mb-8">
                                <div className="flex items-center space-x-4">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-50 text-[#1E56A0]">
                                        {editingItem ? <Edit3 className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
                                    </div>
                                    <h2 className="text-2xl font-bold" style={{ color: '#163172' }}>
                                        {editingItem ? 'Update Content' : 'Create New Entry'}
                                    </h2>
                                </div>
                                {editingItem && (
                                    <button onClick={cancelEdit} className="p-2 rounded-full hover:bg-slate-100 text-slate-400 transition-colors">
                                        <X className="w-6 h-6" />
                                    </button>
                                )}
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                {activeTab === 'news' ? (
                                    <>
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center px-1">
                                                <label className="text-xs font-bold uppercase tracking-widest" style={{ color: '#5a6c7d' }}>Headline</label>
                                                <span className={`text-[10px] font-bold ${newsForm.title.length > 130 ? 'text-red-500' : 'text-slate-400'}`}>{newsForm.title.length}/150</span>
                                            </div>
                                            <input
                                                type="text" required maxLength={150}
                                                value={newsForm.title} onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                                                className="w-full px-5 py-3.5 rounded-2xl border border-[#D6E4F0] focus:ring-2 focus:ring-[#1E56A0]/10 focus:border-[#1E56A0] outline-none transition-all"
                                                placeholder="Catchy news headline..."
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center px-1">
                                                    <label className="text-xs font-bold uppercase tracking-widest" style={{ color: '#5a6c7d' }}>Category</label>
                                                    <span className="text-[10px] font-bold text-slate-400">{newsForm.category.length}/50</span>
                                                </div>
                                                <input
                                                    type="text" required maxLength={50}
                                                    value={newsForm.category} onChange={(e) => setNewsForm({ ...newsForm, category: e.target.value })}
                                                    className="w-full px-5 py-3.5 rounded-2xl border border-[#D6E4F0] focus:ring-2 focus:ring-[#1E56A0]/10 focus:border-[#1E56A0] outline-none transition-all"
                                                    placeholder="e.g. Research"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center px-1">
                                                    <label className="text-xs font-bold uppercase tracking-widest" style={{ color: '#5a6c7d' }}>Publication Date</label>
                                                </div>
                                                <div className="relative">
                                                    <Calendar className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a6c7d] pointer-events-none" />
                                                    <input
                                                        type="date" required
                                                        value={newsForm.date} onChange={(e) => setNewsForm({ ...newsForm, date: e.target.value })}
                                                        className="w-full pl-12 pr-5 py-3.5 rounded-2xl border border-[#D6E4F0] focus:ring-2 focus:ring-[#1E56A0]/10 focus:border-[#1E56A0] outline-none transition-all"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center px-1">
                                                <label className="text-xs font-bold uppercase tracking-widest" style={{ color: '#5a6c7d' }}>Link / URL</label>
                                            </div>
                                            <div className="relative">
                                                <LinkIcon className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a6c7d] pointer-events-none" />
                                                <input
                                                    type="url"
                                                    value={newsForm.url} onChange={(e) => setNewsForm({ ...newsForm, url: e.target.value })}
                                                    className="w-full pl-12 pr-5 py-3.5 rounded-2xl border border-[#D6E4F0] focus:ring-2 focus:ring-[#1E56A0]/10 focus:border-[#1E56A0] outline-none transition-all"
                                                    placeholder="https://..."
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center px-1">
                                                <label className="text-xs font-bold uppercase tracking-widest" style={{ color: '#5a6c7d' }}>Summary</label>
                                                <span className={`text-[10px] font-bold ${newsForm.summary.length > 900 ? 'text-red-500' : 'text-slate-400'}`}>{newsForm.summary.length}/1000</span>
                                            </div>
                                            <textarea
                                                required rows="4" maxLength={1000}
                                                value={newsForm.summary} onChange={(e) => setNewsForm({ ...newsForm, summary: e.target.value })}
                                                className="w-full px-5 py-3.5 rounded-2xl border border-[#D6E4F0] focus:ring-2 focus:ring-[#1E56A0]/10 focus:border-[#1E56A0] outline-none resize-none transition-all"
                                                placeholder="Type a brief overview of the story..."
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center px-1">
                                                <label className="text-xs font-bold uppercase tracking-widest" style={{ color: '#5a6c7d' }}>Study / Paper Title</label>
                                                <span className={`text-[10px] font-bold ${researchForm.title.length > 180 ? 'text-red-500' : 'text-slate-400'}`}>{researchForm.title.length}/200</span>
                                            </div>
                                            <input
                                                type="text" required maxLength={200}
                                                value={researchForm.title} onChange={(e) => setResearchForm({ ...researchForm, title: e.target.value })}
                                                className="w-full px-5 py-3.5 rounded-2xl border border-[#D6E4F0] focus:ring-2 focus:ring-[#1E56A0]/10 focus:border-[#1E56A0] outline-none transition-all"
                                                placeholder="The impact of..."
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center px-1">
                                                    <label className="text-xs font-bold uppercase tracking-widest" style={{ color: '#5a6c7d' }}>Academic Source</label>
                                                    <span className="text-[10px] font-bold text-slate-400">{researchForm.source.length}/100</span>
                                                </div>
                                                <input
                                                    type="text" required maxLength={100}
                                                    value={researchForm.source} onChange={(e) => setResearchForm({ ...researchForm, source: e.target.value })}
                                                    className="w-full px-5 py-3.5 rounded-2xl border border-[#D6E4F0] focus:ring-2 focus:ring-[#1E56A0]/10 focus:border-[#1E56A0] outline-none transition-all"
                                                    placeholder="e.g. Science Journal"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center px-1">
                                                    <label className="text-xs font-bold uppercase tracking-widest" style={{ color: '#5a6c7d' }}>Release Year</label>
                                                </div>
                                                <input
                                                    type="number" required min="1900" max="2100"
                                                    value={researchForm.year} onChange={(e) => setResearchForm({ ...researchForm, year: e.target.value })}
                                                    className="w-full px-5 py-3.5 rounded-2xl border border-[#D6E4F0] focus:ring-2 focus:ring-[#1E56A0]/10 focus:border-[#1E56A0] outline-none transition-all"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center px-1">
                                                <label className="text-xs font-bold uppercase tracking-widest" style={{ color: '#5a6c7d' }}>Link / URL</label>
                                            </div>
                                            <div className="relative">
                                                <LinkIcon className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a6c7d] pointer-events-none" />
                                                <input
                                                    type="url"
                                                    value={researchForm.url} onChange={(e) => setResearchForm({ ...researchForm, url: e.target.value })}
                                                    className="w-full pl-12 pr-5 py-3.5 rounded-2xl border border-[#D6E4F0] focus:ring-2 focus:ring-[#1E56A0]/10 focus:border-[#1E56A0] outline-none transition-all"
                                                    placeholder="Link to DOI or full publication..."
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center px-1">
                                                <label className="text-xs font-bold uppercase tracking-widest" style={{ color: '#5a6c7d' }}>Summary</label>
                                                <span className={`text-[10px] font-bold ${researchForm.summary.length > 1400 ? 'text-red-500' : 'text-slate-400'}`}>{researchForm.summary.length}/1500</span>
                                            </div>
                                            <textarea
                                                required rows="6" maxLength={1500}
                                                value={researchForm.summary} onChange={(e) => setResearchForm({ ...researchForm, summary: e.target.value })}
                                                className="w-full px-5 py-3.5 rounded-2xl border border-[#D6E4F0] focus:ring-2 focus:ring-[#1E56A0]/10 focus:border-[#1E56A0] outline-none resize-none transition-all"
                                                placeholder="Key research findings..."
                                            />
                                        </div>
                                    </>
                                )}

                                {message.text && (
                                    <div className={`p-5 rounded-2xl text-sm flex items-start space-x-3 animate-in fade-in slide-in-from-top-2 duration-300 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                                        {message.type === 'success' ? <CheckCircle2 className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
                                        <span>{message.text}</span>
                                    </div>
                                )}

                                <Button
                                    disabled={submitting}
                                    className="w-full py-7 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition-all active:scale-[0.98] flex items-center justify-center space-x-2"
                                    style={{ background: 'linear-gradient(135deg, #1E56A0 0%, #163172 100%)', color: '#FFFFFF' }}
                                >
                                    {submitting ? <Loader2 className="w-6 h-6 animate-spin" /> : <span>{editingItem ? 'Publish Updates' : 'Publish to Live'}</span>}
                                </Button>
                            </form>
                        </div>
                    )}

                    {/* List Card */}
                    <div className="bg-white rounded-[32px] shadow-sm border border-[#D6E4F0] overflow-hidden flex flex-col min-h-[600px]">
                        <div className="p-8 border-b border-[#D6E4F0] bg-slate-50 flex justify-between items-center">
                            <div>
                                <h2 className="text-xl font-bold" style={{ color: '#163172' }}>{activeTab === 'messages' ? 'Recent Inquiries' : 'Active Content'}</h2>
                                <p className="text-sm text-slate-400">Total: {items.length} items</p>
                            </div>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    placeholder="Filter..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9 pr-4 py-2 rounded-xl border border-[#D6E4F0] text-sm focus:ring-1 focus:ring-[#1E56A0] outline-none"
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-auto max-h-[800px]">
                            {loading ? (
                                <div className="p-32 flex flex-col items-center justify-center text-[#5a6c7d]">
                                    <Loader2 className="w-12 h-12 animate-spin mb-6" style={{ color: '#1E56A0' }} />
                                    <p className="font-medium">Curating your workspace...</p>
                                </div>
                            ) : filteredItems.length === 0 ? (
                                <div className="p-32 text-center text-[#5a6c7d]">
                                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <AlertCircle className="w-8 h-8 text-slate-300" />
                                    </div>
                                    <p className="font-medium">No records found yet.</p>
                                    <p className="text-sm">Try a different search or add a new entry.</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-[#D6E4F0]">
                                    {filteredItems.map((item) => (
                                        <div key={item.id} className={`p-8 hover:bg-slate-50 transition-all flex justify-between items-start group ${editingItem?.id === item.id ? 'bg-blue-50/70 border-l-4 border-[#1E56A0]' : ''}`}>
                                            <div className="flex-1 min-w-0 pr-6">
                                                <div className="flex items-center space-x-3 mb-2">
                                                    <span className="px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest bg-white text-[#1E56A0] border border-blue-100 shadow-sm">
                                                        {activeTab === 'news' ? item.category : activeTab === 'research' ? item.year : item.role}
                                                    </span>
                                                    {(activeTab === 'news' || activeTab === 'messages') && (
                                                        <span className="text-[10px] font-bold text-slate-400 flex items-center">
                                                            {activeTab === 'news' ? (
                                                                <><Clock className="w-3 h-3 mr-1.5" /> {item.read_time} Mins</>
                                                            ) : (
                                                                <><User className="w-3 h-3 mr-1.5" /> {item.name}</>
                                                            )}
                                                        </span>
                                                    )}
                                                    <span className="text-[10px] font-bold text-slate-400 border-l border-slate-200 pl-3">
                                                        {activeTab === 'news' ? item.date : activeTab === 'research' ? item.source : item.email}
                                                    </span>
                                                </div>
                                                <h3 className="font-bold text-[#163172] text-lg leading-snug mb-3 group-hover:text-[#1E56A0] transition-colors">
                                                    {activeTab === 'messages' ? `Inquiry: ${item.name}` : item.title}
                                                </h3>
                                                <p className="text-sm text-slate-500 line-clamp-2 leading-relaxed font-medium">
                                                    {activeTab === 'messages' ? item.message : item.summary}
                                                </p>
                                                {activeTab === 'messages' && (
                                                    <div className="mt-4 text-[10px] font-bold text-slate-400 flex items-center">
                                                        <Calendar className="w-3 h-3 mr-1.5" /> {new Date(item.timestamp).toLocaleString()}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex space-x-2 pt-1">
                                                {activeTab !== 'messages' && (
                                                    <button
                                                        onClick={() => startEdit(item)}
                                                        title="Edit"
                                                        className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-300 hover:text-[#1E56A0] hover:bg-white hover:shadow-md transition-all active:scale-95"
                                                    >
                                                        <Edit3 className="w-5 h-5" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleDelete(item.id)}
                                                    title="Delete"
                                                    className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-300 hover:text-red-600 hover:bg-red-50 transition-all active:scale-95"
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <footer className="mt-auto py-8 text-center border-t border-[#D6E4F0]">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Sage and Seed Console &copy; 2025</p>
            </footer>
        </div>
    );
};

export default Dashboard;
